import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Callout from "components/callout/Callout";
import DropFilter from "components/dropdown/DropFilter";
import GiftCardsTable from "./GiftCardsTable";
import Search from "components/search/Search";
import FileImport from "components/fileImport/FileImport";

import "pages/secure/facility/order/order.scss";
import "pages/secure/facility/order/giftCard.scss";
import { GetGiftCard, IGiftCard, ImportGiftCards } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";

interface IFilterState {
  search: string;
  all: Array<Record<string, any>>;
  status: string[];
  openBalanceCheck: boolean;
  detailedSearch: string;
  viewDetailedInfo: boolean;
}

export default function GiftCards() {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const { authStore, facilityStore } = useAppSelector(store => store);

  const permissions = authStore.user?.permissions;
  const facilityPermissions = facilityStore.facility?.permissions;

  const [giftCards, setGiftCards] = useState<IGiftCard[]>(undefined);

  const [filterState, setFilterState] = useState<IFilterState>({
    search: "",
    all: undefined,
    status: [],
    openBalanceCheck: false,
    detailedSearch: null,
    viewDetailedInfo: false,
  });

  const { state: fileImportModal, updateModal, closeModal } = useModal();

  const statusFilters = [
    {
      id: "valid",
      label: t("secure.facility.order.gift_cards.014"),
    },
    {
      id: "disabled",
      label: t("secure.facility.order.gift_cards.015"),
    },
  ];

  // Search giftcards
  useEffect(() => {
    if (filterState === undefined) {
      return;
    }

    const source = axios.CancelToken.source();
    void loadGiftCards(source.token);

    return () => {
      source.cancel();
    };
  }, [filterState.search, filterState.all]);

  async function loadGiftCards(token: CancelToken) {
    if (giftCards !== undefined) {
      setGiftCards(undefined);
    }
    const giftcardRes = permissions.gift_cards_view
      ? await GetGiftCard({ search: filterState.search, filters: filterState.all }, token ? false : true, token)
      : await GetGiftCard({ code: filterState.search, filters: filterState.all }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (giftcardRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_cards.004")));
    }

    setGiftCards(giftcardRes.status !== StatusCode.OK ? [] : giftcardRes.data);
  }

  async function importGiftCards(selectedFile: File) {
    const formData = new FormData();
    formData.append("file", selectedFile);

    const importRes = await ImportGiftCards(formData, true);

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.gift_cards.016")));
      closeModal();
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.gift_cards.017")));
    closeModal();
  }

  function handleClearFilter(groupId: string) {
    const allFilters: Record<string, any> = filterState.all;

    const removeFilter = allFilters.filter((filter: { label: string }) => filter?.label !== groupId);
    setFilterState(prev => ({
      ...prev,
      [groupId]: [],
      all: removeFilter,
    }));
  }

  function handleApplyFilters(filterReturn: Array<Record<string, any>>, dataGroup: string) {
    if (filterReturn.length === 0) {
      handleClearFilter(dataGroup);
      return;
    }

    const filterValues = filterReturn.map(filter => filter.id as string | number);
    const activeFilterGroup = filterState.all ? filterState.all.filter(group => group.label !== dataGroup) : [];

    activeFilterGroup.push({
      label: dataGroup,
      value: filterValues,
    });

    setFilterState({ ...filterState, all: activeFilterGroup, [dataGroup]: filterValues });
  }

  return (
    <>
      <Page
        title={t("secure.facility.order.gift_cards.002")}
        narrow
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("secure.facility.order.gift_cards.018"),
                handler: () => updateModal({ isOpen: true }),
                disabled: !permissions?.gift_cards_import || !facilityPermissions?.gift_cards_import,
              },
              {
                type: "handler",
                label: t("secure.facility.order.gift_cards.019"),
                handler: () => {},
              },
            ],
          },
        }}
      >
        <div className="flex flex-row flex-wrap gap-4 mb-4">
          <DropFilter
            key="status"
            title={t("secure.facility.order.gift_cards.020")}
            filterData={statusFilters}
            filterLabelPropFromData="label"
            filterIdPropFromData="id"
            filterType="Checkbox"
            applyFilters={(filterReturn: Record<string, any>[]) => handleApplyFilters(filterReturn, "status")}
            leftAligned
            disableDropdown={!permissions.gift_cards_view}
          />
        </div>
        <div className="flex-grow mb-4">
          <Search
            historyKey={"facility_gift_cards_search"}
            searchCallback={searchValue => {
              setFilterState(prevState => ({ ...prevState, search: searchValue }));
            }}
            placeholder={t("secure.facility.order.gift_cards.021")}
          />
        </div>

        {!permissions.gift_cards_view && (
          <Card>
            <Callout
              type="info"
              title={t("secure.facility.order.gift_cards.022")}
              content={t("secure.facility.order.gift_cards.023")}
            />
          </Card>
        )}
        <GiftCardsTable giftCards={giftCards} viewPermission={permissions.gift_cards_view} />
      </Page>

      <FileImport onCancel={closeModal} onOk={importGiftCards} open={fileImportModal.isOpen} />
    </>
  );
}
